import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useWhitelist } from "../hooks/useWhitelist";
import type { User } from "firebase/auth";

interface Props {
  currentUser: User;
}

export function WhitelistManager({ currentUser }: Props) {
  const { entries, loading, error, load, add, remove } = useWhitelist();
  const [email, setEmail] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!email.trim()) return;
    setAdding(true);
    try {
      await add(email.trim().toLowerCase());
      setEmail("");
      toast.success(`${email} added to whitelist`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (e: string) => {
    try {
      await remove(e);
      toast.success(`${e} removed`);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <div className="space-y-6 max-w-lg">
      <div>
        <h2 className="text-sm font-medium mb-1">Allowed users</h2>
        <p className="text-xs text-muted-foreground">Only whitelisted emails can sign in to the dashboard.</p>
      </div>

      <div className="flex gap-2">
        <Input
          placeholder="colleague@circlesup.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleAdd()}
          className="flex-1"
        />
        <Button onClick={handleAdd} disabled={adding || !email.trim()} size="sm" className="gap-1.5 shrink-0">
          <UserPlus className="h-3.5 w-3.5" />
          Add
        </Button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-10 rounded-lg bg-muted/50 animate-pulse" />
          ))}
        </div>
      )}

      {!loading && entries.length > 0 && (
        <div className="rounded-xl border border-border overflow-hidden">
          {entries.map((entry, i) => (
            <div
              key={entry.email}
              className={`flex items-center justify-between px-4 py-2.5 group ${i < entries.length - 1 ? "border-b border-border" : ""}`}
            >
              <div>
                <p className="text-sm">{entry.email}</p>
                <p className="text-[10px] text-muted-foreground">
                  Added {new Date(entry.addedAt).toLocaleDateString()} by {entry.addedBy}
                </p>
              </div>
              {entry.email !== currentUser.email && (
                <button
                  type="button"
                  onClick={() => handleRemove(entry.email)}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-muted opacity-0 group-hover:opacity-100 transition-all"
                  title="Remove"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && entries.length === 0 && (
        <p className="text-sm text-muted-foreground">No entries yet.</p>
      )}
    </div>
  );
}
